import * as React from 'react';
import Button from 'components/src/button';
import { Heading, Paragraph } from 'components/src/typography';
import { OrderPackageFormFieldsFragment } from 'types/generated';
import ISKFormat from '../common/ISKFormat';
import Checkbox from '../../../../../packages/components/src/checkbox';
import QuantitySelect from '../common/QuantitySelect';
import clsx from 'clsx';
import { gridGapClasses } from '@/styles/grid';
import { motion } from 'framer-motion';
import { colors } from 'components/src/foundation';
import Cart from 'components/src/svg/Cart';
import useIsMobile from '@/hooks/useIsMobile';
import ChevronRight from 'components/src/svg/ChevronRight';
import ChevronDown from 'components/src/svg/ChevronDown';
import { AddOnWithQuantity } from '@/models/PackageTypes';
import { formatingPrice, getTotalPrice } from './PackageFormUtils';
import { addOnsQuantityAtom, isPackageFormOpenAtom } from '@/store';
import { useAtom, useSetAtom } from 'jotai';

type CartProps = {
  data: OrderPackageFormFieldsFragment;
  hasAddons: boolean;
  addOns: Record<string, AddOnWithQuantity>;
  singleAddOns: AddOnWithQuantity[];
  multiAddOns: AddOnWithQuantity[];
  isCartOpen: boolean;
  isFormVisible: boolean;
  packageQuantity: number;
  setIsCartOpen: (isOpen: boolean) => void;
  handleCheckboxChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
};

const PackageCart = ({
  data,
  addOns,
  hasAddons,
  singleAddOns,
  multiAddOns,
  isCartOpen,
  packageQuantity,
  setIsCartOpen,
  handleCheckboxChange,
}: CartProps) => {
  const isMobileSize = useIsMobile();
  const setAddOnsQuantity = useSetAtom(addOnsQuantityAtom);
  const [isFormVisible, setIsFormVisible] = useAtom(isPackageFormOpenAtom);

  return (
    <motion.div
      className={clsx(
        'pointer-events-none fixed bottom-24 left-0 right-0 z-50 mx-16 flex justify-center md:left-auto md:right-24 md:mx-0'
      )}
    >
      <motion.div
        layout="position"
        transition={{ duration: 0.2 }}
        id="cart-position"
        className={clsx('pointer-events-auto', isCartOpen && 'absolute -top-16 md:-left-16')}
      >
        <motion.div
          key={packageQuantity}
          initial={{ scale: 1.2 }}
          animate={{ scale: 1 }}
          className={clsx('relative', isFormVisible && 'opacity-0')}
        >
          <Button
            icon={
              isCartOpen ? (
                isMobileSize ? (
                  <ChevronDown stroke={colors.red[100]} />
                ) : (
                  <ChevronRight color={colors.red[100]} />
                )
              ) : (
                <Cart color={colors.primary.white} />
              )
            }
            label=""
            variant={isCartOpen ? 'surface' : 'primary'}
            classes={clsx('card-elevation-1 ', isCartOpen ? '!h-40 !p-8' : '!p-16')}
            onClick={() => {
              if (hasAddons) {
                setIsCartOpen(!isCartOpen);
              } else {
                setIsFormVisible(true);
              }
            }}
          />
          {!isCartOpen && packageQuantity > 0 && (
            <div className="label-sm absolute right-0 top-0 flex h-16 w-16 items-center justify-center rounded-full bg-white-75 text-red-100">
              {packageQuantity}
            </div>
          )}
        </motion.div>
      </motion.div>
      {isCartOpen && (
        <motion.div layout className="pointer-events-auto max-w-[430px] overflow-hidden rounded-28 bg-white-50">
          <motion.div layout="position" className="col-span-full grid gap-y-16 rounded-28 p-24 md:col-span-6">
            <div className="rounded-16 bg-monochrome-2">
              {hasAddons && (
                <Heading variant="xs" classes="label-lg px-16 py-8 !font-bold text-red-100">
                  {'Viðbætur'}
                </Heading>
              )}
              <div className={clsx(gridGapClasses, 'grid grid-cols-1 gap-y-16')}>
                <div className="col-start-1 flex flex-col gap-16">
                  {singleAddOns.map(
                    ({ addOn }) =>
                      addOn.id && (
                        <Checkbox
                          key={addOn.id}
                          label={`${addOn.title} ${addOn.price ? formatingPrice(addOn.price) : ''}`}
                          id={addOn.id}
                          onChange={handleCheckboxChange}
                          checked={addOns[addOn.id]?.quantity > 0}
                          large
                        />
                      )
                  )}
                </div>
                <div className="flex flex-col gap-16">
                  {multiAddOns.map(({ addOn, quantity }) => (
                    <div className="flex flex-row items-center justify-between gap-8 p-16 md:flex-row" key={addOn.id}>
                      <Paragraph variant="label">{`${addOn.title} ${
                        addOn.price ? formatingPrice(addOn.price) : ''
                      }`}</Paragraph>
                      <QuantitySelect
                        value={quantity}
                        onChange={(changeValue) => {
                          if (addOn.id) {
                            setAddOnsQuantity({ id: addOn.id, change: changeValue });
                          }
                        }}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="col-span-full grid w-full grid-flow-col items-center gap-16 md:justify-between">
              <div className="grid grid-flow-row items-start justify-self-center md:items-end">
                {data.packagePrice && (
                  <>
                    <Paragraph variant="xs" classes="text-primary-faded-text-100">
                      {'kr. á mánuði'}
                    </Paragraph>
                    <ISKFormat
                      value={getTotalPrice(addOns, data.packagePrice || 0)}
                      renderText={(value) => (
                        <motion.div key={packageQuantity} initial={{ scale: 1.1 }} animate={{ scale: 1 }}>
                          <Heading classes="headline-sm !font-bold" variant="xs">
                            {value}
                          </Heading>
                        </motion.div>
                      )}
                    />
                  </>
                )}
              </div>
              <div className="flex flex-col items-center gap-16 lg:flex-row">
                <Button
                  autoWidth={false}
                  label={'Panta'}
                  variant="primary"
                  onClick={() => {
                    setIsCartOpen(false);
                    setIsFormVisible(true);
                  }}
                />
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </motion.div>
  );
};

export default PackageCart;
